import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Box, Center, HStack, Heading, Link, Text, VStack, Wrap, StackProps, SkeletonText, HeadingProps } from '@chakra-ui/react';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import React from 'react';
import { LuArrowUpRight } from 'react-icons/lu';
export type InvestorSearchCardProps = {
  investorSearchId: string;
  showTags?: boolean;
  stackProps?: StackProps;
  companyNameSize?: HeadingProps['size'];
  ctaType?: 'edit' | 'open';
};
export const InvestorSearchCard: React.FC<InvestorSearchCardProps> = props => {
  const $ = _c(47);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($investorSearchId: String!) {
        scoInvestorSearch(investorSearchId: $investorSearchId) {
          id
          investorSearchId
          title
          companyName
          fundingGoalInDollars
          relevantInvestorTags
          requiredInvestorTags
          groupRequest {
            id
            title
            description
            groupRequestId
          }
          investorSearchTags {
            id
            strength
            investorTag {
              id
              title
              investorTagTypeKey
            }
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.investorSearchId) {
    t1 = {
      variables: {
        investorSearchId: props.investorSearchId
      }
    };
    $[1] = props.investorSearchId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const investorSearch = query.data?.scoInvestorSearch;
  const cardHref = investorSearch?.groupRequest ? `/requests/manage/${investorSearch.groupRequest.groupRequestId}` : `/investors/matching/${props.investorSearchId}`;
  if (query.loading) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Box bgColor="blackAlpha.300" borderRadius="md" padding={4} width="full"><SkeletonText flexGrow={1} gap="4" lineClamp={3} /></Box>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (query.error || !investorSearch) {
    let t2;
    if ($[4] !== query.error) {
      t2 = query.error?.toString() || "Not Found";
      $[4] = query.error;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t2) {
      t3 = <Center padding={4}><Text>{"Error: "}{t2}</Text></Center>;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      bgColor: "colorPalette.subtle"
    };
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== cardHref || $[10] !== router) {
    t3 = () => {
      router.push(cardHref);
    };
    $[9] = cardHref;
    $[10] = router;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = investorSearch?.groupRequest ? "Funding Request" : "Fundraise";
  let t5;
  if ($[12] !== t4) {
    t5 = <Badge variant="solid">{t4}</Badge>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== cardHref || $[15] !== investorSearch.groupRequest || $[16] !== props.ctaType || $[17] !== props.investorSearchId) {
    t6 = props.ctaType === "open" ? <ButtonLink href={cardHref} size="xs" variant="outline" onClick={_temp}>Open<LuArrowUpRight /></ButtonLink> : <Text fontSize="2xs" opacity={0.5} textStyle="label"><Link asChild={true}><NextLink href={investorSearch?.groupRequest ? `/requests/manage/${investorSearch?.groupRequest.groupRequestId}/edit` : `/investors/matching/${props.investorSearchId}/settings`} onClick={_temp2}>Edit</NextLink></Link></Text>;
    $[14] = cardHref;
    $[15] = investorSearch.groupRequest;
    $[16] = props.ctaType;
    $[17] = props.investorSearchId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <HStack gap={2} justify="space-between">{t5}{t6}</HStack>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = props.companyNameSize || "md";
  const t9 = `/investors/matching/${props.investorSearchId}`;
  const t10 = investorSearch.companyName || "Untitled Company";
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <Link asChild={true}><NextLink href={t9}>{t10}</NextLink></Link>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8) {
    t12 = <Heading size={t8}>{t11}</Heading>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== investorSearch.fundingGoalInDollars) {
    t13 = investorSearch.fundingGoalInDollars > 0 && <span>{"Raising "}<strong>${investorSearch.fundingGoalInDollars?.toLocaleString()}</strong></span>;
    $[28] = investorSearch.fundingGoalInDollars;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <Text color="colorPalette.fg/50" marginTop={2} textStyle="sm">{t13}</Text>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <Box>{t12}{t14}</Box>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== investorSearch.investorSearchTags || $[36] !== investorSearch.relevantInvestorTags || $[37] !== props.showTags) {
    t16 = props.showTags && investorSearch.investorSearchTags && <Wrap gap={1}>{investorSearch.relevantInvestorTags?.map(_temp3)}</Wrap>;
    $[35] = investorSearch.investorSearchTags;
    $[36] = investorSearch.relevantInvestorTags;
    $[37] = props.showTags;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16 || $[41] !== t7) {
    t17 = <VStack align="stretch" gap={3} padding={4}>{t7}{t15}{t16}</VStack>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t7;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== props.stackProps || $[44] !== t17 || $[45] !== t3) {
    t18 = <VStack align="stretch" bgColor="colorPalette.bg" border="1px solid" borderColor="colorPalette.border" borderRadius="md" boxShadow="xs" cursor="pointer" gap={0} justify="center" width="full" _hover={t2} onClick={t3} {...props.stackProps}>{t17}</VStack>;
    $[43] = props.stackProps;
    $[44] = t17;
    $[45] = t3;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
};
function _temp(ev) {
  ev.stopPropagation();
}
function _temp2(ev_0) {
  ev_0.stopPropagation();
}
function _temp3(tag, idx) {
  return <Badge key={idx} animation="2s ease-in bgOpacity" bgColor="colorPalette.subtle" color="colorPalette.fg" fontFamily="Poppins" fontSize="10px" fontWeight={600} lineHeight={1} opacity={parseInt(tag[1]) / 100} paddingX={1} paddingY={1} variant="solid">{tag[0]}</Badge>;
}